import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';

export interface AltaCosturaCollection {
  id: number;
  name: string;
  year: string;
  description: string;
  featured: boolean;
  image: string;
  images: string[];
  sort_order: number;
  created_at?: string;
  updated_at?: string;
}

export function useSupabaseCollections(initialData: AltaCosturaCollection[]) {
  const initialDataRef = useRef(initialData);
  const [data, setData] = useState<AltaCosturaCollection[]>(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const TIMEOUT_MS = 3000;
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      setLoading(false);
      setError('Tiempo de espera agotado. Usando datos locales.');
    }, TIMEOUT_MS);

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: rows, error: err } = await supabase
          .from('alta_costura_collections')
          .select('*')
          .order('sort_order', { ascending: true });
        if (timedOut) return;
        clearTimeout(timer);
        if (err) {
          setError(err.message);
          setData(initialDataRef.current);
        } else if (rows && rows.length > 0) {
          setData(
            rows.map((row: Record<string, unknown>) => ({
              id: row.id as number,
              name: (row.name as string) ?? '',
              year: (row.year as string) ?? '',
              description: (row.description as string) ?? '',
              featured: (row.featured as boolean) ?? false,
              image: (row.image as string) ?? '',
              images: (row.images as string[]) ?? [],
              sort_order: (row.sort_order as number) ?? 0,
              created_at: (row.created_at as string) ?? '',
              updated_at: (row.updated_at as string) ?? '',
            }))
          );
        } else {
          setData(initialDataRef.current);
        }
      } catch {
        if (timedOut) return;
        clearTimeout(timer);
        setError('Error de conexión');
        setData(initialDataRef.current);
      } finally {
        if (!timedOut) setLoading(false);
      }
    };
    fetchData();
    return () => clearTimeout(timer);
  }, []);

  const add = useCallback(
    async (item: Omit<AltaCosturaCollection, 'id'>) => {
      setLoading(true);
      try {
        const payload = {
          name: item.name,
          year: item.year,
          description: item.description,
          featured: item.featured,
          image: item.image,
          images: item.images,
          sort_order: item.sort_order ?? 0,
        };
        const { data: inserted, error: err } = await supabase
          .from('alta_costura_collections')
          .insert(payload)
          .select()
          .single();
        if (err) {
          setError(err.message);
          return;
        }
        if (inserted) {
          const newItem: AltaCosturaCollection = {
            id: inserted.id as number,
            name: (inserted.name as string) ?? '',
            year: (inserted.year as string) ?? '',
            description: (inserted.description as string) ?? '',
            featured: (inserted.featured as boolean) ?? false,
            image: (inserted.image as string) ?? '',
            images: (inserted.images as string[]) ?? [],
            sort_order: (inserted.sort_order as number) ?? (item.sort_order ?? 0),
            created_at: (inserted.created_at as string) ?? '',
            updated_at: (inserted.updated_at as string) ?? '',
          };
          setData((prev) => [...prev, newItem].sort((a, b) => a.sort_order - b.sort_order));
          setHasChanges(true);
        }
      } catch {
        setError('Error al crear colección');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const update = useCallback(
    async (id: number, updates: Partial<AltaCosturaCollection>) => {
      setLoading(true);
      try {
        const { data: updated, error: err } = await supabase
          .from('alta_costura_collections')
          .update(updates)
          .eq('id', id)
          .select()
          .single();
        if (err) {
          setError(err.message);
          return;
        }
        if (updated) {
          const updatedItem: AltaCosturaCollection = {
            id: updated.id as number,
            name: (updated.name as string) ?? '',
            year: (updated.year as string) ?? '',
            description: (updated.description as string) ?? '',
            featured: (updated.featured as boolean) ?? false,
            image: (updated.image as string) ?? '',
            images: (updated.images as string[]) ?? [],
            sort_order: (updated.sort_order as number) ?? 0,
            created_at: (updated.created_at as string) ?? '',
            updated_at: (updated.updated_at as string) ?? '',
          };
          setData((prev) =>
            prev.map((item) => (item.id === id ? updatedItem : item))
          );
          setHasChanges(true);
        }
      } catch {
        setError('Error al actualizar colección');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const remove = useCallback(
    async (id: number) => {
      setLoading(true);
      try {
        const { error: err } = await supabase
          .from('alta_costura_collections')
          .delete()
          .eq('id', id);
        if (err) {
          setError(err.message);
          return;
        }
        setData((prev) => prev.filter((item) => item.id !== id));
        setHasChanges(true);
      } catch {
        setError('Error al eliminar colección');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const updateOrder = useCallback(async (orderedIds: number[]) => {
    setLoading(true);
    try {
      const updates = orderedIds.map((id, idx) =>
        supabase
          .from('alta_costura_collections')
          .update({ sort_order: idx })
          .eq('id', id)
      );
      const results = await Promise.all(updates);
      const firstErr = results.find((r) => r.error)?.error;
      if (firstErr) {
        setError(firstErr.message);
        return;
      }
      setData((prev) =>
        [...prev].sort(
          (a, b) => orderedIds.indexOf(a.id) - orderedIds.indexOf(b.id)
        )
      );
      setHasChanges(true);
    } catch {
      setError('Error al reordenar colecciones');
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: rows, error: err } = await supabase
        .from('alta_costura_collections')
        .select('*')
        .order('sort_order', { ascending: true });
      if (err) {
        setError(err.message);
      } else if (rows) {
        setData(
          rows.map((row: Record<string, unknown>) => ({
            id: row.id as number,
            name: (row.name as string) ?? '',
            year: (row.year as string) ?? '',
            description: (row.description as string) ?? '',
            featured: (row.featured as boolean) ?? false,
            image: (row.image as string) ?? '',
            images: (row.images as string[]) ?? [],
            sort_order: (row.sort_order as number) ?? 0,
            created_at: (row.created_at as string) ?? '',
            updated_at: (row.updated_at as string) ?? '',
          }))
        );
      }
    } catch {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, add, update, remove, updateOrder, refresh, loading, error, hasChanges, setHasChanges };
}